import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Building2, CheckCircle2, ChevronRight, Store, UserSearch } from "lucide-react";
import { cn } from "@/lib/cn";
import { useNeedsReview } from "@/hooks/useNeedsReview";
import type { NeedsReviewRow, NeedsReviewScope } from "@/api/needsReview";

const SCOPE_LABEL: Record<NeedsReviewScope, string> = {
  my: "Мої",
  team: "Команда",
  all: "Усі",
};

const BRANCH_LABEL: Record<string, string> = {
  breeder: "Розплідник",
  commercial_non_breeder: "Комерційний",
};

const MODEL_LABEL: Record<string, string> = {
  breeder_professional: "Професійний розплідник",
  retail: "Роздріб",
  wholesale: "Опт",
  dealer: "Дилер",
  veterinary: "Ветеринар",
  farm: "Ферма",
  other: "Інше",
};

const PARTY_LABEL: Record<string, string> = {
  organization: "Організація",
  physical_person: "Фізособа / ФОП",
};

/**
 * Phase 3.X-1B sub_3 — NeedsReview QUEUE ("Перегляд профілів").
 *
 * Cursor-paged list of business profiles awaiting A3 Gate v2 review. Backend
 * owns scope (Manager→my · Supervisor→team · Director→all), cursor pagination,
 * branch + branch-specific missing count, and row visibility (TZ §7/§E5). Rows
 * open the A3 gate at /clients/:holdingId/business-profile (NOT the legacy
 * "5 universal fields"). Replaces the old StubDialog→crmold bounce.
 */
export function NeedsReviewQueuePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Supervisor Home C2 deep-link (?scope=team) — initial scope from URL once;
  // backend tier-revalidates (manager→appliedScope=my, no broaden).
  const [scope, setScope] = useState<NeedsReviewScope | "">(() => {
    const s = searchParams.get("scope");
    return s === "team" || s === "my" || s === "all" ? s : "";
  });
  const [cursor, setCursor] = useState("");
  const [accumulated, setAccumulated] = useState<NeedsReviewRow[]>([]);

  const query = useNeedsReview({ scope, cursor, limit: 25 });
  const data = query.data;

  const allowedScopes = data?.allowedScopes ?? ["my"];
  const effectiveScope: NeedsReviewScope = (scope || data?.appliedScope || "my") as NeedsReviewScope;

  const filterKey = `${scope}`;
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    setLastFilterKey(filterKey);
    setCursor("");
    setAccumulated([]);
  }

  const rows = useMemo(() => {
    if (!data) return accumulated;
    if (cursor === "") return data.items;
    const seen = new Set(accumulated.map((r) => r.profileId));
    return [...accumulated, ...data.items.filter((r) => !seen.has(r.profileId))];
  }, [data, cursor, accumulated]);

  function loadMore() {
    if (data?.nextCursor) {
      setAccumulated(rows);
      setCursor(data.nextCursor);
    }
  }

  function openRow(row: NeedsReviewRow) {
    if (row.holdingId) navigate(`/clients/${row.holdingId}/business-profile`);
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-[1080px]">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <UserSearch className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Перегляд профілів</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Бізнес-профілі, що потребують перевірки менеджером. Найстаріші — згори.
            </p>
          </div>
        </div>

        <div className="mb-3 rounded-2xl border bg-card p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border bg-background p-0.5">
              {allowedScopes.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={cn(
                    "rounded px-3 py-1 text-xs font-medium transition-colors",
                    effectiveScope === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {SCOPE_LABEL[s]}
                </button>
              ))}
            </div>
            <span className="ml-auto text-sm text-muted-foreground">
              <b className="text-foreground">{data?.totalVisibleCount ?? 0}</b> у черзі
            </span>
          </div>
        </div>

        {query.isPending ? (
          <ListSkeleton />
        ) : query.isError ? (
          <div className="rounded-2xl border bg-card px-4 py-12 text-center">
            <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
            <div className="text-sm font-medium">Не вдалося завантажити чергу</div>
            <button onClick={() => query.refetch()} className="mt-3 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground">
              Повторити
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
            <CheckCircle2 className="mx-auto mb-2 h-6 w-6 text-success-foreground" />
            <div className="text-sm font-medium">Немає профілів на перевірку</div>
            <div className="mt-1 text-sm text-muted-foreground">У цій області всі профілі переглянуто.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <NeedsReviewRowCard key={r.profileId} row={r} onOpen={() => openRow(r)} />
            ))}
          </div>
        )}

        {data && rows.length > 0 && (
          <div className="mt-3 flex items-center justify-center gap-3">
            <span className="text-sm text-muted-foreground">
              Показано <b className="text-foreground">{rows.length}</b> із <b className="text-foreground">{data.totalVisibleCount}</b>
            </span>
            {data.nextCursor && (
              <button
                onClick={loadMore}
                disabled={query.isFetching}
                className="rounded-md border bg-background px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
              >
                {query.isFetching ? "Завантаження…" : "Показати ще"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function NeedsReviewRowCard({ row, onOpen }: { row: NeedsReviewRow; onOpen: () => void }) {
  const Icon = row.partyType === "physical_person" ? Store : Building2;
  const model = row.primaryBusinessModel ? (MODEL_LABEL[row.primaryBusinessModel] ?? row.primaryBusinessModel) : "";
  const party = PARTY_LABEL[row.partyType];
  return (
    <button
      onClick={onOpen}
      disabled={!row.holdingId}
      className="flex w-full items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left enabled:hover:border-primary/40 disabled:cursor-default"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[15px] font-semibold">{row.clientDisplayName}</span>
          <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
            {BRANCH_LABEL[row.branch] ?? row.branch}
          </span>
          {row.missingRequiredCount > 0 && (
            <span className="shrink-0 rounded-full bg-warning px-2 py-0.5 text-[11px] font-medium text-warning-foreground">
              {row.missingRequiredCount} полів бракує
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {party && <span>{party}</span>}
          {model && <><span>·</span><span>{model}</span></>}
          {row.manager && <><span>·</span><span>{row.manager}</span></>}
          {row.updatedAt && <><span>·</span><span>{row.updatedAt.slice(0, 10)}</span></>}
        </div>
      </div>

      {row.holdingId && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />}
    </button>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl border bg-card px-4 py-3">
          <div className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-2.5 w-1/4 animate-pulse rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}
